"use client";

import { c as _c } from "react/compiler-runtime";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useCallback, useState } from "react";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import WaveDropActions from "../WaveDropActions";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import ParticipationDropContainer from "./ParticipationDropContainer";
import ParticipationDropHeader from "./ParticipationDropHeader";
import ParticipationDropContent from "./ParticipationDropContent";
import ParticipationDropMetadata from "./ParticipationDropMetadata";
import ParticipationDropFooter from "./ParticipationDropFooter";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
import type { DropInteractionParams, DropLocation } from "../drop.types";
interface OngoingParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
export default function OngoingParticipationDrop(t0) {
  const $ = _c(75);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick
  } = t0;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  const hasTouch = useIsTouchDevice() || isMobile;
  let identityProfile;
  let t1;
  let visibleMetadata;
  if ($[0] !== drop.author || $[1] !== drop.metadata || $[2] !== drop.wave) {
    identityProfile = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    let t2;
    if ($[6] !== drop.metadata || $[7] !== drop.wave) {
      t2 = getParticipationVisibleMetadata({
        wave: drop.wave,
        metadata: drop.metadata
      });
      $[6] = drop.metadata;
      $[7] = drop.wave;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    visibleMetadata = t2;
    t1 = identityProfile ? areSameProfileIdentity({
      left: drop.author,
      right: identityProfile
    }) : false;
    $[0] = drop.author;
    $[1] = drop.metadata;
    $[2] = drop.wave;
    $[3] = identityProfile;
    $[4] = t1;
    $[5] = visibleMetadata;
  } else {
    identityProfile = $[3];
    t1 = $[4];
    visibleMetadata = $[5];
  }
  const isSelfNominee = t1;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  let t2;
  if ($[9] !== hasTouch) {
    t2 = () => {
      if (!hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[9] = hasTouch;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const handleLongPress = t2;
  let t3;
  if ($[11] !== activePartIndex || $[12] !== drop || $[13] !== onReply) {
    t3 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[11] = activePartIndex;
    $[12] = drop;
    $[13] = onReply;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const handleOnReply = t3;
  let t4;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsSlideUp(false);
    };
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleOnAddReaction = t4;
  let t5;
  if ($[16] !== canShowVote || $[17] !== drop) {
    t5 = canShowVote ? <VotingModalButton drop={drop} onClick={() => setIsVotingModalOpen(true)} /> : null;
    $[16] = canShowVote;
    $[17] = drop;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const voteAction = t5;
  let t6;
  if ($[19] !== activePartIndex || $[20] !== drop || $[21] !== handleOnReply || $[22] !== isMobile || $[23] !== showReplyAndQuote) {
    t6 = !isMobile && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[19] = activePartIndex;
    $[20] = drop;
    $[21] = handleOnReply;
    $[22] = isMobile;
    $[23] = showReplyAndQuote;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== drop) {
    t7 = <WaveDropAuthorPfp drop={drop} />;
    $[25] = drop;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== drop || $[28] !== showWaveInfo) {
    t8 = <ParticipationDropHeader drop={drop} showWaveInfo={showWaveInfo} />;
    $[27] = drop;
    $[28] = showWaveInfo;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== activePartIndex || $[31] !== drop || $[32] !== handleLongPress || $[33] !== onDropContentClick || $[34] !== onQuoteClick) {
    t9 = <ParticipationDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} />;
    $[30] = activePartIndex;
    $[31] = drop;
    $[32] = handleLongPress;
    $[33] = onDropContentClick;
    $[34] = onQuoteClick;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== t8 || $[37] !== t9) {
    t10 = <div className="tw-flex tw-w-full tw-flex-col">{t8}{t9}</div>;
    $[36] = t8;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== t10 || $[40] !== t7) {
    t11 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-px-4 tw-pt-4 tw-text-left">{t7}{t10}</div>;
    $[39] = t10;
    $[40] = t7;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== drop.id || $[43] !== identityProfile || $[44] !== isSelfNominee) {
    t12 = identityProfile && <div className="tw-ml-[3.25rem] tw-px-4"><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" showIdentityHeader={!isSelfNominee} /></div>;
    $[42] = drop.id;
    $[43] = identityProfile;
    $[44] = isSelfNominee;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== drop.id || $[47] !== visibleMetadata) {
    t13 = <ParticipationDropMetadata metadata={visibleMetadata} contextId={drop.id} />;
    $[46] = drop.id;
    $[47] = visibleMetadata;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== drop || $[50] !== voteAction) {
    t14 = <ParticipationDropFooter drop={drop} voteAction={voteAction} />;
    $[49] = drop;
    $[50] = voteAction;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== t12 || $[53] !== t13 || $[54] !== t14) {
    t15 = <div className="tw-flex tw-w-full tw-flex-col">{t12}{t13}{t14}</div>;
    $[52] = t12;
    $[53] = t13;
    $[54] = t14;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== drop || $[57] !== isMobileScreen || $[58] !== isVotingModalOpen) {
    t16 = isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />;
    $[56] = drop;
    $[57] = isMobileScreen;
    $[58] = isVotingModalOpen;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== drop || $[61] !== handleOnReply || $[62] !== isSlideUp || $[63] !== longPressTriggered || $[64] !== showReplyAndQuote) {
    t17 = <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[60] = drop;
    $[61] = handleOnReply;
    $[62] = isSlideUp;
    $[63] = longPressTriggered;
    $[64] = showReplyAndQuote;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  let t18;
  if ($[66] !== drop || $[67] !== isActiveDrop || $[68] !== location || $[69] !== t11 || $[70] !== t15 || $[71] !== t16 || $[72] !== t17 || $[73] !== t6) {
    t18 = <ParticipationDropContainer drop={drop} isActiveDrop={isActiveDrop} location={location}>{t6}{t11}{t15}{t16}{t17}</ParticipationDropContainer>;
    $[66] = drop;
    $[67] = isActiveDrop;
    $[68] = location;
    $[69] = t11;
    $[70] = t15;
    $[71] = t16;
    $[72] = t17;
    $[73] = t6;
    $[74] = t18;
  } else {
    t18 = $[74];
  }
  return t18;
}